// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module AnalyticsDashboardPage
 * @package @geenius/analytics/solidjs-css
 * @description Composes the primary SolidJS analytics dashboard page for the
 * vanilla-CSS variant. The page exposes a ready-to-render overview surface for
 * traffic and engagement reporting without relying on Tailwind utilities.
 */

import {
  type AnalyticsDashboardData,
  type AnalyticsEvent,
  type CohortAnalysis,
  type FunnelResult,
  type RealtimeStats,
  buildAnalyticsSeoMeta,
} from "@geenius/analytics";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@geenius/ui/solidjs-css";
import { type Component, For, Show } from "solid-js";
import { DeviceBreakdown } from "../components/DeviceBreakdown";
import {
  type AnalyticsErrorStateValue,
  ErrorState,
} from "../components/ErrorState";
import { EventTable } from "../components/EventTable";
import { FunnelChart } from "../components/FunnelChart";
import { RealtimeBadge } from "../components/RealtimeBadge";
import { StatsCard } from "../components/StatsCard";
import { TopPagesTable } from "../components/TopPagesTable";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { FadeIn } from "../lib/FadeIn";

const AnalyticsSeoHead: Component<{ meta: unknown }> = (props) => {
  void props.meta;
  return null;
};

/**
 * Props for rendering the SolidJS CSS analytics dashboard page.
 *
 * @property data Optional aggregate dashboard data rendered across the page sections.
 * @property recentEvents Optional list of recent analytics events shown in the event table.
 * @property funnelResult Optional conversion funnel analysis rendered in the conversion section.
 * @property cohortAnalysis Optional cohort retention analysis rendered in the retention section.
 * @property realtimeStats Optional realtime activity snapshot rendered in the live section.
 * @property isLoading Whether child widgets should render loading placeholders instead of content.
 * @property error Optional page-level error surfaced before dashboard data sections.
 * @property onRetry Optional recovery callback shown in the page-level error state.
 * @property class Optional class name merged into the page wrapper.
 */
export interface AnalyticsDashboardPageProps {
  data?: AnalyticsDashboardData | null;
  recentEvents?: AnalyticsEvent[];
  funnelResult?: FunnelResult | null;
  cohortAnalysis?: CohortAnalysis | null;
  realtimeStats?: RealtimeStats | null;
  isRealtimeLoading?: boolean;
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders the SolidJS CSS analytics dashboard page with summary cards and detail tables.
 *
 * @param props Page props containing dashboard aggregates, recent events, and loading state.
 * @returns A full-page SolidJS analytics dashboard composition.
 */
export const AnalyticsDashboardPage: Component<AnalyticsDashboardPageProps> = (
  props,
) => {
  const copy = createAnalyticsCopy();
  const seoMeta = buildAnalyticsSeoMeta({
    title: copy.t("analytics.seo.dashboardTitle"),
    description: copy.t("analytics.seo.dashboardDescription"),
    path: "/analytics",
    keywords: ["analytics", "traffic", "engagement", "funnels", "events"],
  });
  const funnelResult = (): FunnelResult =>
    props.funnelResult ?? {
      name: copy.t("analytics.dashboard.conversions"),
      steps: [],
      totalEntries: 0,
      totalConversions: 0,
      overallConversionRate: 0,
      analyzedAt: 0,
    };
  const cohortBuckets = () => props.cohortAnalysis?.cohorts ?? [];

  return (
    <>
      <AnalyticsSeoHead meta={seoMeta} />
      <div
        class={cx("gn-analytics-page", props.class)}
        data-analytics-page="dashboard"
      >
        <FadeIn>
          <header class="gn-analytics-page__header" data-analytics-page-header>
            <h1 class="gn-analytics-page__title">
              {copy.t("analytics.dashboard.title")}
            </h1>
            <p class="gn-analytics-page__subtitle">
              {copy.t("analytics.dashboard.subtitle")}
            </p>
          </header>
        </FadeIn>

        <Show when={props.error}>
          <FadeIn delay={60}>
            <Card data-analytics-page-error>
              <CardContent>
                <ErrorState error={props.error} onRetry={props.onRetry} />
              </CardContent>
            </Card>
          </FadeIn>
        </Show>

        <FadeIn
          delay={60}
          class="gn-analytics-dashboard__kpi-grid"
          data-analytics-stats-row
        >
          <StatsCard
            label={copy.t("analytics.dashboard.pageViews")}
            value={props.data?.totalPageViews.toLocaleString() ?? "—"}
            icon="👁️"
          />
          <StatsCard
            label={copy.t("analytics.dashboard.uniqueVisitors")}
            value={props.data?.uniqueVisitors.toLocaleString() ?? "—"}
            icon="👤"
          />
          <StatsCard
            label={copy.t("analytics.dashboard.topReferrers")}
            value={props.data?.topReferrers?.length?.toString() ?? "0"}
            icon="🔗"
          />
          <StatsCard
            label={copy.t("analytics.dashboard.devices")}
            value={Object.keys(
              props.data?.browserBreakdown ?? {},
            ).length.toString()}
            icon="📊"
          />
        </FadeIn>

        <FadeIn delay={120}>
          <h2 class="gn-analytics-a11y__sr-only">
            {copy.t("analytics.dashboard.overview")}
          </h2>
          <Card data-analytics-overview-section>
            <CardHeader>
              <CardTitle data-analytics-section-title>
                {copy.t("analytics.dashboard.overview")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                class="gn-analytics-dashboard__chart-grid"
                data-analytics-grid
              >
                <TopPagesTable
                  pages={props.data?.topPages ?? []}
                  isLoading={props.isLoading}
                />
                <DeviceBreakdown
                  breakdown={
                    props.data?.deviceBreakdown ?? {
                      desktop: 0,
                      tablet: 0,
                      mobile: 0,
                    }
                  }
                />
              </div>
            </CardContent>
          </Card>
        </FadeIn>

        <FadeIn
          delay={150}
          class="gn-analytics-dashboard__chart-grid"
          data-analytics-conversion-section
        >
          <FunnelChart result={funnelResult()} />
          <Card
            class="gn-analytics-dashboard-widget gn-analytics-dashboard-widget--cohort"
            data-analytics-cohort-section
          >
            <CardHeader>
              <CardTitle data-analytics-section-title>
                {copy.t("analytics.dashboard.cohorts")}
              </CardTitle>
              <CardDescription>
                {props.cohortAnalysis?.name ??
                  copy.t("analytics.dashboard.cohortDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                class="gn-analytics-dashboard-widget__meta"
                data-analytics-cohort-overview
              >
                <Badge variant="secondary">
                  {copy.plural(
                    "analytics.dashboardBuilder.cohorts",
                    cohortBuckets().length,
                  )}
                </Badge>
                <Badge variant="secondary" data-analytics-cohort-intervals>
                  {copy.t("analytics.dashboardBuilder.dayIntervals", {
                    intervals:
                      props.cohortAnalysis?.intervals.join(", ") ??
                      copy.t("analytics.dashboard.noIntervals"),
                  })}
                </Badge>
              </div>
              <Show
                when={cohortBuckets().length > 0}
                fallback={
                  <p class="gn-analytics-page__subtitle">
                    {copy.t("analytics.dashboard.noCohorts")}
                  </p>
                }
              >
                <div
                  class="gn-analytics-cohort__grid"
                  data-analytics-cohort-grid
                >
                  <For each={cohortBuckets().slice(0, 4)}>
                    {(cohort) => (
                      <Card class="gn-analytics-cohort__card" shadow="none">
                        <CardContent>
                          <strong>{cohort.cohort}</strong>
                          <div>
                            {copy.plural(
                              "analytics.dashboardBuilder.users",
                              cohort.size,
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </For>
                </div>
              </Show>
            </CardContent>
          </Card>
          <Card
            class="gn-analytics-dashboard-widget gn-analytics-dashboard-widget--realtime"
            data-analytics-realtime-section
          >
            <CardHeader>
              <CardTitle data-analytics-section-title>
                {copy.t("analytics.dashboard.realtime")}
              </CardTitle>
              <CardDescription>
                {copy.t("analytics.dashboard.realtimeDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RealtimeBadge
                stats={props.realtimeStats ?? null}
                isLoading={props.isRealtimeLoading ?? props.isLoading ?? false}
              />
            </CardContent>
          </Card>
        </FadeIn>

        <FadeIn delay={180}>
          <h2 class="gn-analytics-a11y__sr-only">
            {copy.t("analytics.dashboard.recentEvents")}
          </h2>
          <Card data-analytics-section>
            <CardHeader>
              <CardTitle data-analytics-section-title>
                {copy.t("analytics.dashboard.recentEvents")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <EventTable
                events={props.recentEvents ?? []}
                isLoading={props.isLoading}
                maxRows={15}
              />
            </CardContent>
          </Card>
        </FadeIn>
      </div>
    </>
  );
};
